import './style.scss';

import React, { useMemo } from 'react';

import { useGlobalContext } from '../../../context/global';
import { useNudgeEngine } from '../../../hooks/useNudgeEngine';
import { usePulseChecklist } from '../../../hooks/usePulseChecklist';
import { usePulseEventMachine } from '../../../hooks/usePulseEventMachine';
import { useUnitLock } from '../../../hooks/useUnitLock';
import { type IElectionEventsPanelData, PU_EVENT_ORDER } from '../../../interface/pu-events';
import { CopyValue } from '../../utils/copy';
import { RefreshSVG } from '../../utils/svgs';
import { clockTime } from '../pu-cancellation/shared';
import PulseSummary from './summary';
import PulseTimeline from './timeline';

/**
 * Election Day Pulse — the full right-section panel: the 8-milestone vertical
 * timeline for the agent's polling unit, with one-tap capture (10s undo),
 * offline queueing, photo evidence and schedule-aware overdue badges.
 */
const ElectionEventsPanel = () => {
	const { rsProps, actions, locationTracking } = useGlobalContext();

	const data = (rsProps?.data ?? {}) as Partial<IElectionEventsPanelData>;
	const { puCode, puName, electionId, electionName } = data;

	const lock = useUnitLock(electionId, puCode, true);
	const paused = lock.locked;
	const checklist = usePulseChecklist(electionId, puCode, { cancelledAt: paused ? lock.cancelledAt : undefined });
	const machine = usePulseEventMachine({
		puCode,
		electionId,
		electionName,
		actions,
		locationCache: locationTracking
			? { latitude: locationTracking.latitude, longitude: locationTracking.longitude, accuracy: locationTracking.accuracy }
			: null,
		onQueueChanged: checklist.reloadQueue,
		onConfirmed: checklist.applyLocalConfirm,
		paused,
	});
	// The panel IS the timeline — suppress toasts while it's open.
	const nudge = useNudgeEngine(checklist.slots, actions, { suppressToasts: true });

	const progressPct = useMemo(
		() => Math.round((checklist.capturedCount / PU_EVENT_ORDER.length) * 100),
		[checklist.capturedCount]
	);

	if (!puCode || !electionId) {
		return (
			<div className='pue-panel pue-panel--empty'>
				<p className='pue-panel__empty-text'>No polling unit assignment found for this timeline.</p>
			</div>
		);
	}

	return (
		<div className='pue-panel'>
			<header className='pue-panel__header'>
				<div className='pue-panel__header-row'>
					<div className='pue-panel__title-wrap'>
						<h4 className='pue-panel__title'>Election Day Pulse</h4>
						<p className='pue-panel__subtitle'>
							{puName || 'Polling Unit'} · {puCode}
							<CopyValue
								value={puCode}
								label='polling unit code'
								size={14}
							/>
						</p>
						{electionName ? <p className='pue-panel__election'>{electionName}</p> : null}
					</div>
					<div className='pue-panel__progress-pill'>
						{checklist.capturedCount}/{PU_EVENT_ORDER.length}
					</div>
				</div>
				<div
					className='pue-panel__bar'
					role='progressbar'
					aria-valuenow={checklist.capturedCount}
					aria-valuemin={0}
					aria-valuemax={PU_EVENT_ORDER.length}
					aria-label='Events captured'
				>
					<span
						className='pue-panel__bar-fill'
						style={{ width: `${progressPct}%` }}
					/>
				</div>
				{checklist.queueLength > 0 ? (
					<button
						type='button'
						className='pue-panel__queue-banner'
						onClick={() => void checklist.flushQueue()}
					>
						{checklist.queueLength} capture{checklist.queueLength > 1 ? 's' : ''} waiting to sync — tap to retry
					</button>
				) : null}
				{paused ? (
					<p
						className='pue-panel__paused'
						role='status'
					>
						<strong>Paused.</strong> INEC cancelled this unit
						{clockTime(lock.cancelledAt) ? ` at ${clockTime(lock.cancelledAt)}` : ''}. Your captured milestones are kept; capture
						resumes if your party reinstates the unit.
					</p>
				) : null}
				{checklist.load === 'offline' ? (
					<p className='pue-panel__offline-note'>
						You appear to be offline — captures are kept on this device and sync automatically.
					</p>
				) : null}
			</header>

			{checklist.capturedCount >= PU_EVENT_ORDER.length ? <PulseSummary slots={checklist.slots} /> : null}

			{checklist.load === 'loading' && checklist.capturedCount === 0 ? (
				<div className='pue-panel__loading'>
					<span
						className='pue-capture__spinner'
						aria-hidden='true'
					/>
					<p>Loading your timeline…</p>
				</div>
			) : (
				<PulseTimeline
					slots={checklist.slots}
					nextExpected={paused ? null : nudge.nextExpected}
					nextExpectedAt={paused ? null : nudge.nextExpectedAt}
					// A cancelled unit never reaches the remaining milestones — they are not overdue.
					overdue={paused ? [] : nudge.overdue}
					captureState={machine.state}
					onCapture={machine.arm}
					onUndo={machine.undo}
					onRetry={machine.retry}
					onDismiss={machine.dismiss}
					onFlush={() => void checklist.flushQueue()}
					onAttachPhoto={machine.attachPhoto}
					paused={paused}
				/>
			)}

			<footer className='pue-panel__footer'>
				<button
					type='button'
					className='pue-panel__refresh'
					onClick={() => void checklist.refresh()}
				>
					<RefreshSVG />
					Refresh from server
				</button>
				<p className='pue-panel__footer-note'>
					Timestamps are recorded the moment you tap. Each event can only be captured once and is shared with your party’s
					situation room.
				</p>
			</footer>
		</div>
	);
};

export default ElectionEventsPanel;
